import { cn } from '@/shared/helpers/classnames'
import { type Size } from '@/shared/types/size'
import { type IconProps, WithIcon } from '@/shared/ui/icon'
import { type TooltipProps, useToolTip } from '@/shared/ui/tooltip'

import {
  Children,
  type ComponentProps,
  type MouseEvent,
  type PropsWithChildren,
  type ReactElement,
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useState,
} from 'react'
import { Link } from 'react-router-dom'

/**
 * Props for the Button component.
 *
 * @property intent - Button role: 'primary', 'secondary', 'accent', 'neutral'.
 * @property variant - Button visual style: 'default', 'outline', 'soft', 'link', 'ghost'.
 * @property size - Button size: 'xs', 'sm', 'md', 'lg', 'xl'.
 * @property icon - Optional icon (legacy syntax). Use children for new syntax.
 * @property label - Button label content (legacy syntax). Use children for new syntax.
 * @property loading - If true, shows loading spinner and disables actions.
 * @property onClick - Supports async functions: the button automatically enters "loading" state during execution. As a link, it has no loading state, runs before navigation and can cancel it with `event.preventDefault()` called before any `await`.
 * @property to - Link destination (used when renderAs="link").
 * @property disabled - If true, disables the button.
 * @property className - Additional CSS classes.
 * @property renderAs - Render as 'link' (React Router Link) or 'button' (HTML button). Default: 'link' if 'to' is provided, otherwise 'button'. It is only needed if you use onClick to navigate to a route.
 * @property children - New syntax: Pass icon element and text directly. Icon position is determined by order (icon first = left, icon last = right).
 * @property ...restProps - Other props from HTMLButtonElement or Link.
 *
 * @example
 * // New syntax with icon as children (recommended)
 * <Button variant="outline" intent="neutral" size="sm">
 *   <Send />
 *   Resend
 * </Button>
 *
 * @example
 * // Icon right position
 * <Button variant="default" intent="primary" size="md">
 *   Submit
 *   <ArrowRight />
 * </Button>
 *
 * @example
 * // Legacy syntax (still supported)
 * <Button
 *   label="Resend"
 *   variant="outline"
 *   intent="neutral"
 *   size="sm"
 *   icon={{ component: Send, position: "left" }}
 * />
 */
export type ButtonProps = Omit<ComponentProps<'button'>, 'onClick'> &
  Partial<ComponentProps<typeof Link>> &
  PropsWithChildren<{
    /** Button role */
    intent?: 'primary' | 'secondary' | 'accent' | 'neutral'
    /** Button visual style */
    variant?: 'default' | 'outline' | 'soft' | 'link' | 'ghost'
    /** Button size */
    size?: Size
    /** @deprecated Optional icon (legacy syntax) - Use children for new syntax */
    icon?: IconProps
    /** @deprecated Button label content (legacy syntax) - Use children for new syntax */
    label?: string
    /** If true, shows loading spinner and disables actions */
    loading?: boolean
    /**
     * Supports async functions: the button automatically enters "loading" state during execution.
     * When rendered as a link, there is no loading state: it runs before navigation, and can cancel
     * it with `event.preventDefault()` called before any `await`; without `to`, the link never
     * navigates by itself.
     */
    onClick?: (event: MouseEvent) => void | Promise<unknown>
    /** renderAs - Render as 'link' (React Router Link) or 'button' (HTML button). Default: 'link' if 'to' is provided, otherwise 'button'. It is only needed if you use onClick to navigate to a route. **/
    renderAs?: 'link' | 'button'
    /** Tooltip properties */
    tooltip?: TooltipProps
  }>

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      variant = 'default',
      intent = 'primary',
      size = 'md',
      disabled = false,
      loading: rawLoading = false,
      className,
      renderAs,
      to,
      icon,
      label: rawLabel,
      onClick: rawOnClick,
      tooltip,
      children,
      target = '_self',
      ...restProps
    },
    ref,
  ) => {
    const { onClick, loading: onClickLoading } = useOnClick({
      onClick: rawOnClick,
      disabled,
    })

    const tooltipProps = useToolTip(tooltip ?? {})

    const isLoadingState = rawLoading || onClickLoading

    // Parse all button content (handles both legacy and new syntax)
    const {
      iconElement: iconNode,
      textContent: label,
      iconPosition,
    } = prepareButtonContent({
      children,
      label: rawLabel,
      icon,
      size,
      loading: isLoadingState,
    })

    const commonProps = {
      className: cn('btn', className, {
        // No dynamic class for tailwind compatibility
        'btn-loading': isLoadingState,
        'btn-disabled': disabled,
        'btn-xs': size === 'xs',
        'btn-sm': size === 'sm',
        'btn-md': size === 'md',
        'btn-lg': size === 'lg',
        'btn-xl': size === 'xl',
        'btn-default': variant === 'default',
        'btn-outline': variant === 'outline',
        'btn-soft': variant === 'soft',
        'btn-link': variant === 'link',
        'btn-ghost': variant === 'ghost',
        'btn-primary': intent === 'primary',
        'btn-secondary': intent === 'secondary',
        'btn-accent': intent === 'accent',
        'btn-neutral': intent === 'neutral',
        'icon-only': !label && iconNode,
      }),
      children: (
        <WithIcon position={iconPosition} icon={iconNode}>
          {label && <span>{label}</span>}
        </WithIcon>
      ),

      ...tooltipProps,
    }

    const isLink = renderAs === 'link' || to
    const disableActions = disabled || isLoadingState

    if (isLink) {
      // The handler runs before the Link navigates, so it can still cancel the navigation with
      // `event.preventDefault()`. Without `to`, the Link only points at `#`: the handler owns the
      // navigation, and letting the Link follow would push the current page back on top of it.
      // It is called directly, not through `useOnClick`: the loading state it sets would re-render
      // the Link with `to="#"` before the browser follows a modifier-click or `target="_blank"`.
      const onLinkClick = (event: MouseEvent) => {
        void rawOnClick?.(event)
        if (!to) event.preventDefault()
      }

      return (
        <Link
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
          to={disableActions ? '#' : (to ?? '#')}
          aria-disabled={disableActions}
          target={target}
          rel="noopener noreferrer"
          {...(restProps as Omit<ComponentProps<typeof Link>, 'to'>)}
          {...commonProps}
          onClick={disableActions ? e => e.preventDefault() : onLinkClick}
        />
      )
    }

    return (
      <button
        ref={ref as React.ForwardedRef<HTMLButtonElement>}
        disabled={disabled}
        type="button"
        {...(restProps as ComponentProps<'button'>)}
        onClick={isLoadingState ? undefined : onClick}
        {...commonProps}
      />
    )
  },
)

Button.displayName = 'Button'

/**
 * Prepare button content (icon and text) from either legacy props or new children syntax
 * Handles both syntaxes and unifies icon/text extraction logic
 *
 * @param children - New syntax: icon element and text as React children
 * @param label - Legacy syntax: text label as prop
 * @param icon - Legacy syntax: icon configuration as prop
 * @param size - Button size (affects icon size)
 * @param loading - Whether button is in loading state (replaces icon with spinner)
 * @returns Object with iconElement, textContent, and iconPosition
 */
const prepareButtonContent = ({
  children,
  label,
  icon,
  size,
  loading,
}: {
  children: React.ReactNode
  label?: string
  icon?: IconProps
  size: Size
  loading: boolean
}): {
  iconElement: React.ReactNode
  textContent: React.ReactNode
  iconPosition: 'left' | 'right' | undefined
} => {
  // Icon size mapping
  const iconSizeBySize: Record<Size, number> = {
    xl: 18,
    lg: 16,
    md: 14,
    sm: 12,
    xs: 10,
  }

  const iconSize = iconSizeBySize[size]

  // Helper to create loading spinner
  const createSpinner = () => (
    <span className="loading loading-spinner" style={{ width: iconSize, height: iconSize }} />
  )

  // Legacy syntax: icon and label as props
  if (icon || label) {
    const IconComponent = icon?.component
    const position = icon?.position

    // Create icon element (spinner if loading, otherwise icon component)
    let iconElement = null
    if (loading) {
      iconElement = createSpinner()
    } else if (IconComponent) {
      iconElement = <IconComponent size={iconSize} />
    }

    return {
      iconElement,
      textContent: label || children,
      iconPosition: position,
    }
  }

  // New syntax: icon and text as children
  if (!children) {
    return { iconElement: null, textContent: null, iconPosition: undefined }
  }

  const childrenArray = Children.toArray(children)

  // Find icon element (valid React element, typically from react-bootstrap-icons)
  const iconIndex = childrenArray.findIndex(child => isValidElement(child))

  if (iconIndex === -1) {
    // No icon found in children
    // If loading, show spinner at left position by default
    if (loading) {
      return {
        iconElement: createSpinner(),
        textContent: children,
        iconPosition: 'left',
      }
    }

    // No icon and no loading, return text only
    return {
      iconElement: null,
      textContent: children,
      iconPosition: undefined,
    }
  }

  const iconElement = childrenArray[iconIndex] as ReactElement
  const textContent = childrenArray.filter((_, index) => index !== iconIndex)

  // Determine position based on order
  const iconPosition = iconIndex === 0 ? 'left' : 'right'

  // Create styled icon (spinner if loading, otherwise clone icon with size)
  const styledIcon = loading
    ? createSpinner()
    : cloneElement(iconElement, {
        size: iconSize,
      } as Partial<typeof iconElement.props>)

  return {
    iconElement: styledIcon,
    textContent: textContent.length > 0 ? textContent : null,
    iconPosition,
  }
}

const useOnClick = ({
  onClick: rawOnClick,
  disabled,
}: Pick<ButtonProps, 'onClick' | 'disabled'>) => {
  const [clickLoading, setClickLoading] = useState(false)

  const onClick = useCallback(
    (event: MouseEvent) => {
      const asyncOnClick = async (event: MouseEvent) => {
        setClickLoading(true)
        try {
          if (disabled) {
            event.preventDefault()
            return
          }
          if (rawOnClick) {
            await rawOnClick(event)
          }
        } finally {
          setClickLoading(false)
        }
      }
      void asyncOnClick(event)
    },
    [disabled, rawOnClick],
  )

  return { onClick, loading: clickLoading }
}
